import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  useCreateBlockNote,
  FormattingToolbar,
  FormattingToolbarController,
  getFormattingToolbarItems,
  useComponentsContext,
  useBlockNoteEditor,
  useEditorSelectionChange,
} from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/core/fonts/inter.css';
import '@blocknote/core/style.css';
import '@blocknote/mantine/style.css';
import { useTheme } from '../../context/ThemeContext';
import {
  getVision,
  getResource,
  updateResource,
  deleteResource,
} from '../../actions/planningAction';
import { flattenNodes, LinkPicker } from './PlanningLibrary';
import PlanningChat from './PlanningChat';
import './PlanningDocEditor.css';

// Standard ProseMirror algorithm: find the full range of a mark around a position.
function getMarkRangeAtPos($pos, markType) {
  const after = $pos.parent.childAfter($pos.parentOffset);
  if (!after.node) return null;
  const mark = after.node.marks.find(m => m.type === markType);
  if (!mark) return null;
  let startIndex = $pos.index();
  let startPos = $pos.start() + after.offset;
  let endIndex = startIndex + 1;
  let endPos = startPos + after.node.nodeSize;
  while (startIndex > 0 && mark.isInSet($pos.parent.child(startIndex - 1).marks)) {
    startIndex -= 1;
    startPos -= $pos.parent.child(startIndex).nodeSize;
  }
  while (endIndex < $pos.parent.childCount && mark.isInSet($pos.parent.child(endIndex).marks)) {
    endPos += $pos.parent.child(endIndex).nodeSize;
    endIndex += 1;
  }
  return { from: startPos, to: endPos };
}

// "Remove link" toolbar button — only shows when the selection sits on a link.
function RemoveLinkButton() {
  const editor = useBlockNoteEditor();
  const Components = useComponentsContext();
  const [hasLink, setHasLink] = useState(false);

  useEditorSelectionChange(() => {
    setHasLink(!!editor.getSelectedLinkUrl());
  }, editor);

  if (!hasLink) return null;

  const removeLink = () => {
    editor.transact((tr) => {
      const linkType = editor.pmSchema.marks.link;
      if (!linkType) return;
      let { from, to, empty, $from } = tr.selection;
      if (empty) {
        const range = getMarkRangeAtPos($from, linkType);
        if (!range) return;
        from = range.from;
        to = range.to;
      }
      tr.removeMark(from, to, linkType);
    });
    setHasLink(false);
  };

  return (
    <Components.FormattingToolbar.Button
      mainTooltip="Remove link"
      label="Unlink"
      onClick={removeLink}
    />
  );
}

export default function PlanningDocEditor() {
  const { visionId, resourceId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const { milestones } = useSelector(s => s.planning);

  const [resource, setResource] = useState(null);
  const [title, setTitle] = useState('');
  const [links, setLinks] = useState([]);
  const [linkedTodos, setLinkedTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [assistOpen, setAssistOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [docHasContent, setDocHasContent] = useState(false);
  const [applyAction, setApplyAction] = useState(null); // 'Creating' | 'Updating' | null

  const editor = useCreateBlockNote();
  const saveTimer = useRef(null);
  const latest = useRef({ title: '', links: [] });
  const loadedRef = useRef(false);
  latest.current = { title, links };

  const allNodes = useMemo(() => flattenNodes(milestones), [milestones]);
  const nodeById = useMemo(() => Object.fromEntries(allNodes.map(n => [n._id, n])), [allNodes]);

  // Load resource + ensure milestones present for the link picker
  useEffect(() => {
    if (!milestones || milestones.length === 0) dispatch(getVision(visionId)).catch(() => {});
    setLoading(true);
    dispatch(getResource(resourceId))
      .then(async (d) => {
        setResource(d.resource);
        setTitle(d.resource.title || '');
        setLinks((d.resource.linkedNodeIds || []).map(String));
        setLinkedTodos(d.resource.linkedTodos || []);
        // Seed the editor from the stored markdown
        const md = d.resource.body || '';
        setDocHasContent(!!md.trim());
        if (md.trim()) {
          const blocks = await editor.tryParseMarkdownToBlocks(md);
          editor.replaceBlocks(editor.document, blocks);
          const last = editor.document[editor.document.length - 1];
          if (last && last.type === 'table') editor.insertBlocks([{ type: 'paragraph' }], last, 'after');
        }
        loadedRef.current = true;
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [resourceId, visionId, dispatch]); // eslint-disable-line

  const persist = useCallback(async () => {
    const { title: t, links: l } = latest.current;
    let md = '';
    try { md = await editor.blocksToMarkdownLossy(editor.document); } catch (_) {}
    try {
      await dispatch(updateResource(resourceId, { title: t, body: md, linkedNodeIds: l }));
      setSaved(true);
    } catch (_) {}
  }, [dispatch, resourceId, editor]);

  const queueSave = useCallback(() => {
    setSaved(false);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persist, 900);
  }, [persist]);

  useEffect(() => () => { clearTimeout(saveTimer.current); if (loadedRef.current) persist(); }, []); // eslint-disable-line

  // Tables can become the terminal block, leaving nowhere to type after them.
  // Guarantee a trailing empty paragraph so the user can always escape a table.
  const ensureTrailingBlock = useCallback(() => {
    const blocks = editor.document;
    const last = blocks[blocks.length - 1];
    if (last && last.type === 'table') {
      editor.insertBlocks([{ type: 'paragraph' }], last, 'after');
    }
  }, [editor]);

  const onEditorChange = () => {
    if (!loadedRef.current) return;
    ensureTrailingBlock();
    queueSave();
  };

  const toggleLink = (id) => {
    setLinks(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    setTimeout(queueSave, 0);
  };

  const remove = async () => {
    clearTimeout(saveTimer.current);
    try { await dispatch(deleteResource(resourceId)); } catch (_) {}
    navigate(`/dashboard/planning/${visionId}`);
  };

  const openAssist = () => {
    if (!assistOpen) persist(); // flush pending edits so Dmyor Assist reasons over the live doc
    setAssistOpen(v => !v);
  };

  // Apply an AI-produced document body into the editor and persist it.
  const applyDocBody = async (md, newTitle) => {
    const verb = docHasContent ? 'Updating' : 'Creating';
    setApplyAction(verb);
    try {
      // Let the overlay paint before the (synchronous) block replace.
      await new Promise(r => setTimeout(r, 50));
      if (md && md.trim()) {
        const blocks = await editor.tryParseMarkdownToBlocks(md);
        editor.replaceBlocks(editor.document, blocks);
        const last = editor.document[editor.document.length - 1];
        if (last && last.type === 'table') editor.insertBlocks([{ type: 'paragraph' }], last, 'after');
        setDocHasContent(true);
      }
      if (newTitle && (!title.trim() || title.trim().toLowerCase() === 'untitled')) {
        latest.current = { ...latest.current, title: newTitle };
        setTitle(newTitle);
      }
      await persist();
    } finally {
      setApplyAction(null);
    }
  };

  const goBack = () => { clearTimeout(saveTimer.current); if (loadedRef.current) persist(); navigate(-1); };

  const WS_TABS = [
    { key: 'dashboard', label: 'Plan' },
    { key: 'execute',   label: 'Execute' },
    { key: 'habits',    label: 'Habits' },
    { key: 'library',   label: 'Library' },
    { key: 'journal',   label: 'Journal' },
  ];

  return (
    <div className={`pde-wrap${theme === 'light' ? ' pde-wrap--light' : ''}`}>
      {/* Workspace nav — a doc lives inside the Library, keep it reachable */}
      <div className="pde-ws-nav">
        <div className="pde-ws-toggle">
          {WS_TABS.map(t => (
            <button
              key={t.key}
              className={`pde-ws-btn${t.key === 'library' ? ' pde-ws-btn--active' : ''}`}
              onClick={() => navigate(`/dashboard/planning/${visionId}?view=${t.key}`)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Top bar */}
      <div className="pde-topbar">
        <button className="pde-back" onClick={goBack}>Back</button>
        <span className="pde-save-state">{loading ? '' : saved ? 'Saved' : 'Saving…'}</span>
        <div className="pde-assist-wrap">
          <button
            className={`pde-generate${assistOpen ? ' pde-generate--open' : ''}`}
            onClick={openAssist}
            disabled={loading}
            title="Chat with Dmyor Assist about this document"
          >
            ✦ Dmyor Assist
          </button>
        </div>
        <button className="pde-del" onClick={() => setConfirmDelete(true)} title="Delete document">Delete</button>
      </div>

      {confirmDelete && (
        <div className="pde-confirm-overlay" onMouseDown={() => setConfirmDelete(false)}>
          <div className="pde-confirm" onMouseDown={e => e.stopPropagation()}>
            <h3 className="pde-confirm-title">Delete this document?</h3>
            <p className="pde-confirm-text">
              "{title || 'Untitled'}" will be permanently removed. This cannot be undone.
            </p>
            <div className="pde-confirm-actions">
              <button className="pde-confirm-cancel" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="pde-confirm-go" onClick={remove}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className={`pde-body${assistOpen ? ' pde-body--chat' : ''}`}>
      <div className="pde-inner">
        {/* Title */}
        <input
          className="pde-title"
          value={title}
          placeholder="Untitled document"
          onChange={e => { setTitle(e.target.value); queueSave(); }}
        />

        {/* Linked nodes */}
        <div className="pde-links">
          {links.map(id => {
            const n = nodeById[id];
            return (
              <span key={id} className="pde-chip">
                <span className={`pde-dot pde-dot--${n?.type || 'node'}`} />
                {n ? (n.content.length > 40 ? n.content.slice(0, 40) + '…' : n.content) : 'node'}
                <button className="pde-chip-x" onClick={() => toggleLink(id)}>×</button>
              </span>
            );
          })}
          <div className="pde-chip-add-wrap">
            <button className="pde-chip-add" onClick={() => setShowPicker(v => !v)}>+ Link node</button>
            {showPicker && (
              <LinkPicker allNodes={allNodes} selected={links} onToggle={toggleLink} onClose={() => setShowPicker(false)} />
            )}
          </div>
        </div>

        {/* Linked todos — jump back to the todo in Execute */}
        {linkedTodos.length > 0 && (
          <div className="pde-todos">
            <span className="pde-todos-label">From todo</span>
            {linkedTodos.map(t => (
              <button
                key={t._id}
                className={`pde-todo-chip${t.done ? ' pde-todo-chip--done' : ''}`}
                onClick={() => navigate(`/dashboard/planning/${visionId}?view=execute&todo=${t._id}`)}
                title="Go to this todo in Execute"
              >
                <i className="bx bx-list-check" />
                {t.text.length > 48 ? t.text.slice(0, 48) + '…' : t.text}
                <span className="pde-todo-chip-go">↗</span>
              </button>
            ))}
          </div>
        )}

        {/* WYSIWYG editor */}
        <div className="pde-editor-shell">
          {loading && (
            <div className="pde-thinking">
              <div className="pde-thinking-card">
                <span className="pde-doc-spinner" />
                <div className="pde-thinking-sub">Loading document…</div>
              </div>
            </div>
          )}
          {applyAction && (
            <div className="pde-thinking">
              <div className="pde-thinking-card">
                <span className="pde-thinking-spark">✦</span>
                <div className="pde-thinking-text">
                  {applyAction} your document
                  <span className="pde-thinking-dots"><span>.</span><span>.</span><span>.</span></span>
                </div>
                <div className="pde-thinking-sub">
                  {applyAction === 'Creating' ? 'Writing it into the editor' : 'Applying the changes'}
                </div>
              </div>
            </div>
          )}
          <BlockNoteView
            editor={editor}
            theme={theme === 'light' ? 'light' : 'dark'}
            onChange={onEditorChange}
            formattingToolbar={false}
          >
            <FormattingToolbarController
              formattingToolbar={() => (
                <FormattingToolbar>
                  {getFormattingToolbarItems()}
                  <RemoveLinkButton key="removeLink" />
                </FormattingToolbar>
              )}
            />
          </BlockNoteView>
        </div>
      </div>

      {/* Dmyor Assist chat — the same chat used everywhere else, with this
          document in focus so it can discuss it and write/revise it directly. */}
      {assistOpen && (
        <div className="pde-chat-drawer">
          <button className="pde-chat-close--floating" onClick={() => setAssistOpen(false)} title="Close">✕</button>
          <div className="pde-chat-embed">
            <PlanningChat
              visionId={visionId}
              milestones={milestones}
              resourceId={resourceId}
              onDocUpdate={({ title: t, body }) => applyDocBody(body, t)}
              onRefresh={() => dispatch(getVision(visionId)).catch(() => {})}
              onFocusNode={(id) => navigate(`/dashboard/planning/${visionId}?view=dashboard&focus=${id}`)}
              theme={theme}
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
